import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SlidersHorizontal, Check, ChevronDown, RotateCcw, Search, X } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

export interface ProductFilterOption {
  value: string
  label: string
}

export interface ProductFilterFacet {
  key: string
  label: string
  /**
   * Presentation/behaviour hint. ``variant`` matters most: the admin library
   * reveals variant rows only when a ``variant`` facet is selected. ``field``
   * is a structural column (template/category/status/type).
   */
  kind: 'category' | 'brand' | 'variant' | 'product' | 'field'
  /**
   * Data type of the facet, driving how it renders. ``select`` (default) is a
   * multi-select checkbox list; ``date`` is a single-select preset list
   * (picking one replaces the previous choice); ``text`` is a "contains" box
   * for a free-text column (a registration number, a name, an address) -
   * its value is the typed text. Backend-provided.
   */
  type?: 'select' | 'date' | 'text'
  options: ProductFilterOption[]
}

/** Map of facet key -> selected option values. */
export type ProductFilterValue = Record<string, string[]>

/** Show the "Find a filter" box once the panel has more facets than this. */
const SEARCH_FROM = 8

interface ProductFilterMenuProps {
  facets: ProductFilterFacet[]
  value: ProductFilterValue
  onChange: (value: ProductFilterValue) => void
  /**
   * Extra classes for the trigger button so each surface can align it to its
   * own toolbar. The consumer keeps the default (matches its h-9 search input
   * + view toggle); the admin library passes the compact toolbar style so the
   * trigger sits flush with FILTER / COLUMNS / EXPORT.
   */
  triggerClassName?: string
}

/**
 * The platform's Filters panel (every list, card and the consumer product
 * browser). Each facet is a row; clicking it opens its options right under it,
 * inside the panel - nothing flies out over the page. What is selected shows
 * at the top as removable chips, with Reset. A long list gets a "Find a
 * filter" box, and the panel always fits the screen (it scrolls inside, with
 * the search and chips kept in view). Renders nothing when there are
 * no facets AND no active selection; if filters are applied but the facet list
 * is empty (a transient error or an empty filtered result set) the trigger
 * stays mounted so Reset remains reachable. The caller owns which facets it
 * passes (e.g. the consumer feeds
 * consumer-visible facets, the admin feeds all variant attributes) and how the
 * selected values map to query params.
 */
export function ProductFilterMenu({
  facets,
  value,
  onChange,
  triggerClassName,
}: ProductFilterMenuProps) {
  const { t } = useTranslation()
  const [openKey, setOpenKey] = useState<string | null>(null)
  // Narrows a long facet list by name (shown from SEARCH_FROM facets).
  const [query, setQuery] = useState('')

  const activeCount = Object.values(value).reduce((n, v) => n + v.length, 0)

  // Hide only when there is genuinely nothing to show. If the facet list is
  // empty but filters are still applied (e.g. the list query errored or the
  // filtered result set produced no facets), keep the trigger mounted so the
  // Reset action stays reachable - otherwise the user is stranded with filters
  // they can no longer clear.
  if (!facets.length && activeCount === 0) return null

  function toggle(facetKey: string, optionValue: string, single = false) {
    const current = value[facetKey] ?? []
    // Single-select (e.g. a date preset): picking a new option replaces the
    // previous choice; re-picking the active one clears it.
    const next = single
      ? current.includes(optionValue)
        ? []
        : [optionValue]
      : current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue]
    const merged = { ...value, [facetKey]: next }
    if (!next.length) delete merged[facetKey]
    onChange(merged)
  }

  const chips = facets.flatMap((facet) =>
    (value[facet.key] ?? []).map((v) => ({
      facet,
      value: v,
      label: facet.type === 'text' ? `"${v}"` : (facet.options.find((o) => o.value === v)?.label ?? v),
    })),
  )
  const shown = query.trim()
    ? facets.filter((f) => f.label.toLowerCase().includes(query.trim().toLowerCase()))
    : facets

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setOpenKey(null)
          setQuery('')
        }
      }}
    >
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className={cn(
              'flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-[8px] border-[0.5px] border-solid px-3 text-[13px]',
              activeCount > 0
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                : 'border-[var(--rule)] bg-[var(--bg)] text-[var(--ink)]',
              triggerClassName,
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('register.filters', { defaultValue: 'Filters' })}
            {activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--green)] px-1 text-[11px] text-white">
                {activeCount}
              </span>
            )}
          </Button>
        }
      />
      <PopoverContent
        side="bottom"
        align="end"
        data-testid="filter-panel"
        className="flex max-h-[min(560px,var(--available-height))] w-[320px] flex-col overflow-hidden p-0"
      >
        {/* Kept in view: what is selected, and the search. */}
        <div className="flex flex-none flex-col gap-2 border-b-[0.5px] border-b-[var(--rule)] p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.13em] text-[var(--ink-3)]">
              {t('register.filters', { defaultValue: 'Filters' })}
            </span>
            {activeCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onChange({})
                  setOpenKey(null)
                }}
                className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[6px] px-2 py-1 text-[12px] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('register.reset', { defaultValue: 'Reset' })}
              </Button>
            )}
          </div>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="filter-chips">
              {chips.map((c) => (
                <Badge
                  key={`${c.facet.key}:${c.value}`}
                  variant="outline"
                  className="h-auto max-w-full gap-1 rounded-full border-[var(--green-border)] bg-[var(--green-bg)] py-0.5 pl-2.5 pr-1 text-[12px] font-normal text-[var(--green)]"
                >
                  <span className="truncate">
                    {c.facet.label}: {c.label}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('register.removeFilter', {
                      defaultValue: 'Remove {{name}}',
                      name: `${c.facet.label}: ${c.label}`,
                    })}
                    onClick={() => toggle(c.facet.key, c.value, c.facet.type !== undefined && c.facet.type !== 'select')}
                    className="h-4 w-4 rounded-full p-0 text-[var(--green)] hover:bg-transparent hover:text-[var(--ink)]"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
          {facets.length > SEARCH_FROM && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('register.findFilter', { defaultValue: 'Find a filter' })}
                aria-label={t('register.findFilter', { defaultValue: 'Find a filter' })}
                className="h-8 pl-8 text-[13px]"
              />
            </div>
          )}
        </div>

        {/* The facets; a facet's options open right under it. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {shown.length === 0 && (
            <p className="px-2 py-3 text-[12.5px] text-[var(--ink-3)]">
              {t('register.noFilterMatch', { defaultValue: 'No filter by that name.' })}
            </p>
          )}
          {shown.map((facet) => {
            const selected = value[facet.key] ?? []
            const isOpen = openKey === facet.key
            const single = facet.type === 'date'
            return (
              <div key={facet.key}>
                <div
                  role="menuitem"
                  tabIndex={0}
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  onClick={() => setOpenKey(isOpen ? null : facet.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                      e.preventDefault()
                      setOpenKey(isOpen ? null : facet.key)
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-[7px] px-2.5 py-2 text-[13px] text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]',
                    isOpen && 'bg-[var(--bg-subtle)]',
                  )}
                >
                  <span className={cn('flex-1 truncate', selected.length > 0 && 'font-medium')}>{facet.label}</span>
                  {selected.length > 0 && (
                    <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--green)] px-1 text-[11px] text-white">
                      {selected.length}
                    </span>
                  )}
                  <ChevronDown
                    className={cn('h-4 w-4 shrink-0 text-[var(--ink-3)] transition-transform', isOpen && 'rotate-180')}
                  />
                </div>
                {isOpen && facet.type === 'text' && (
                  <TextFacet
                    label={facet.label}
                    value={selected[0] ?? ''}
                    onApply={(text) => {
                      const merged = { ...value, [facet.key]: text ? [text] : [] }
                      if (!text) delete merged[facet.key]
                      onChange(merged)
                    }}
                  />
                )}
                {isOpen && facet.type !== 'text' && (
                  <div role="menu" aria-label={facet.label} className="mb-1 ml-2 border-l-[0.5px] border-l-[var(--rule)] py-1 pl-2">
                    {facet.options.map((opt) => {
                      const checked = selected.includes(opt.value)
                      return (
                        <div
                          key={opt.value}
                          role={single ? 'menuitemradio' : 'menuitemcheckbox'}
                          aria-checked={checked}
                          tabIndex={0}
                          onClick={() => toggle(facet.key, opt.value, single)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggle(facet.key, opt.value, single)
                            }
                          }}
                          className="flex cursor-pointer items-center gap-2.5 rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center border-[0.5px]',
                              single ? 'rounded-full' : 'rounded-[4px]',
                              checked
                                ? 'border-[var(--green)] bg-[var(--green)] text-white'
                                : 'border-[var(--rule-strong)] bg-[var(--bg)]',
                            )}
                          >
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <span className="flex-1 truncate">{opt.label}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * A "contains" filter for a free-text column: type, then press Enter or
 * Apply. Clearing the box and applying removes the filter.
 */
function TextFacet({
  label,
  value,
  onApply,
}: {
  label: string
  value: string
  onApply: (text: string) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const apply = () => onApply(draft.trim())
  return (
    <div className="mb-1 ml-2 flex items-center gap-1.5 border-l-[0.5px] border-l-[var(--rule)] py-1 pl-2">
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            apply()
          }
        }}
        placeholder={t('register.filterContains', { defaultValue: 'Contains...' })}
        aria-label={t('register.filterContainsLabel', {
          defaultValue: '{{name}} contains',
          name: label,
        })}
        className="h-8 flex-1 text-[13px]"
      />
      <Button type="button" size="sm" variant="outline" onClick={apply} className="h-8 text-[12px]">
        {t('register.applyFilter', { defaultValue: 'Apply' })}
      </Button>
    </div>
  )
}
